import { memoize } from "lodash";
import { computed, observable, runInAction } from "mobx";
import {
  TReceiptsConfigurationData,
  TReceiptsConfigurationKeys
} from "../../../Types";
import getCoreUIStore from "../../core/core-ui-store";
import {
  SUPERVISOR_RECEIPT_DEFAULT_CONFIGURATION,
  ONE_DOT
} from "../../../constants/input-entries-constants";
import { isDefinedArray, isNullOrUndefined, reduce } from "et3-type-guards";
import { getFormmatedDate } from "../../../helpers/et3-date-helper";
import getCorePopUpStore from "../../core/core-popup-store";
import { apiCallGetReceiptsConfigurationData } from "../../../api/supervisor-api/api-call-get-supervisor-cs-receipt-configuration";
import { apiCallInsertReceiptsConfigurationData } from "../../../api/supervisor-api/api-call-insert-supervisor-cs-receipt-configuration";
import { checkInputValue } from "../../../helpers/et3-receipt-compensation-and-calculation/supervisor-receipt-helpers";

class ReceiptsConfigurationStore {
  showReceiptConfigutationDashboard = observable.box<boolean>(false);
  receiptsConfigurationData = observable.box<TReceiptsConfigurationData[]>([]);
  receiptsConfigurationMappedObject = observable.map<
    string,
    TReceiptsConfigurationData
  >();

  setReceiptsConfigurationData = (data: TReceiptsConfigurationData[]) => {
    const [getLastData] = data;
    this.receiptsConfigurationData.set(data);
    this.receiptsConfigurationMappedObject.set(
      "RECEIPT_CONFIGURATION_DASHBOARD",
      getLastData
    );
  };

  setReceiptsConfigurationObjectKey = (
    propertyName: TReceiptsConfigurationKeys,
    value: string
  ) => {
    runInAction(() => {
      const obj = this.receiptsConfigurationMappedObject.get(
        "RECEIPT_CONFIGURATION_DASHBOARD"
      );
      Object.keys(obj).forEach((key) => {
        if (key === propertyName) {
          const sringValue = reduce(() => `${value}`, "");
          const { wrongInputValue, correctInputValue } = checkInputValue(value);
          const inputWithNoDots = sringValue.split(".").length <= ONE_DOT;
          switch (true) {
            case wrongInputValue:
              return;
            case correctInputValue:
              if (inputWithNoDots) {
                obj[key] = parseInt(value);
                return;
              }
              obj[key] = value;
              return;
            default:
              obj[key] = 0;
              return;
          }
        }
      });
      this.receiptsConfigurationMappedObject.set(
        "RECEIPT_CONFIGURATION_DASHBOARD",
        obj
      );
    });
    return;
  };

  isSaveButtonEnabled = computed(() => {
    let [receiptsConfigurationData] = this.receiptsConfigurationData.get();

    const originalData = reduce(
      () => receiptsConfigurationData,
      SUPERVISOR_RECEIPT_DEFAULT_CONFIGURATION
    );
    const editedData = reduce(
      () =>
        this.receiptsConfigurationMappedObject.get(
          "RECEIPT_CONFIGURATION_DASHBOARD"
        ),
      SUPERVISOR_RECEIPT_DEFAULT_CONFIGURATION
    );

    return Object.keys(originalData).some(
      (key) => originalData[key] !== editedData[key]
    );
  });

  getReceiptConfigutationData = async () => {
    getCoreUIStore().setIsLoading("SUPERVISOR_RECEIPT_CONFIGURATION", true);
    const { data, status, code } = await apiCallGetReceiptsConfigurationData();
    getCoreUIStore().setIsLoading("SUPERVISOR_RECEIPT_CONFIGURATION", false);
    if (status === "OK") {
      if (!isNullOrUndefined(data) && isDefinedArray(data)) {
        this.setReceiptsConfigurationData(data);
      } else {
        this.setReceiptsConfigurationData([
          SUPERVISOR_RECEIPT_DEFAULT_CONFIGURATION
        ]);
        getCorePopUpStore().showAlertMessage(
          "NO_RECEIPT_CONFIGURATION_DATA_YET"
        );
      }
    } else {
      this.setReceiptsConfigurationData([]);
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  insertReceiptsConfigurationData = async () => {
    const receiptsConfigurationArray = this.receiptsConfigurationData.get();
    const id = reduce(() => receiptsConfigurationArray[0].id + 1, 0);
    const receiptsConfigurationObject =
      this.receiptsConfigurationMappedObject.get(
        "RECEIPT_CONFIGURATION_DASHBOARD"
      );
    getCoreUIStore().setIsLoading("SUPERVISOR_RECEIPT_CONFIGURATION", true);
    const { status, code } = await apiCallInsertReceiptsConfigurationData(
      receiptsConfigurationObject
    );
    getCoreUIStore().setIsLoading("SUPERVISOR_RECEIPT_CONFIGURATION", false);
    if (status === "OK") {
      getCorePopUpStore().showSuccessMessage(
        "RECEIPT_CONFIGURATION_INSERTED_SUCCESSFULLY"
      );
      const receiptConfigurationArray = [
        ...this.receiptsConfigurationData.get()
      ];
      receiptConfigurationArray.unshift({
        ...receiptsConfigurationObject,
        id: id,
        createdOn: getFormmatedDate(new Date())
      });
      this.setReceiptsConfigurationData(receiptConfigurationArray);
      getCoreUIStore().setIsLoading("SUPERVISOR_RECEIPT_CONFIGURATION", false);
      return;
    } else {
      getCorePopUpStore().showAlertMessage(code);
      getCoreUIStore().setIsLoading("SUPERVISOR_RECEIPT_CONFIGURATION", false);
    }
  };
}
export const getSupervisorReceiptConfigurationStore = memoize(
  () => new ReceiptsConfigurationStore(),
  () => 1
);
